/* Everything that was not a question about the systems: greetings, jokes, tests. Kept apart
 * because they have no evidence score to average and no gap to fix, and because separated
 * they show whether the assistant handles them gracefully.
 */

import type { OffTopic } from '../api/types';
import { BarChart, LineChart } from '../charts/charts';
import { series } from '../charts/chart';
import { Async, ChartOrTable, Grid, PanelCard } from '../components/Panel';
import { Signal } from '../components/Signal';
import { AnswerCell, ClampedText, Table } from '../components/Table';
import { TileRow, type TileSpec } from '../components/Tiles';
import { useApi } from '../hooks/useApi';
import { kindLabel, kindRank, num, pct, plural, shortDate, weekLabel } from '../lib/format';
import { useApp } from '../state/app';

/**
 * How much of the traffic was not the assistant's job, and whether it was handled
 * gracefully. The last tile is the fault worth reporting: a support ticket proposed for
 * "faleminderit" is a bug, not a conversation.
 */
function tilesFor(data: OffTopic, word: (full: string, plain: string) => string): TileSpec[] {
  const offers = data.messages.reduce((total, m) => total + Number(m.ticket_offers || 0), 0);

  return [
    {
      label: word('Non-system messages', 'Not a real question'),
      value: num(data.non_system_turns),
      hint: `of ${plural(data.turns, 'message')} in this period`,
    },
    {
      label: word('Share of all messages', 'Share of everything sent'),
      value: pct(data.share, 1),
      // Rows written before the classifier existed count in the total but in no kind, so
      // the share is a floor rather than a figure whenever there are any.
      hint: data.unclassified_turns
        ? `at least — ${plural(data.unclassified_turns, 'older message')} was never sorted`
        : 'greetings, thanks, and everything out of scope',
    },
    {
      label: word('People', 'People who sent them'),
      value: num(data.users),
      hint: `across ${plural(data.conversations, 'conversation')}`,
    },
    {
      label: word('Ticket offered anyway', 'Offered a support ticket'),
      value: num(offers),
      hint: offers
        ? 'a support ticket proposed for a message that was not a question — worth reporting'
        : 'the assistant offered no ticket for any of these',
    },
  ];
}

export function OfftopicPanel() {
  const { days, system, simple, word } = useApp();
  const query = useApi<OffTopic>('/api/off-topic', { days, system });

  return (
    <Async query={query} shape="page">
      {(data) => {
        // Ordered by count, but with the two "real work" kinds always first, so the chart
        // reads as a split rather than a ranking.
        const kinds = [...data.by_kind].sort((a, b) => kindRank(a.kind) - kindRank(b.kind));

        return (
          <div className="space-y-5">
            <TileRow tiles={tilesFor(data, word)} />

            <Grid>
              <PanelCard
                title="What kind of message arrives"
                hint={
                  <>
                    Every message by kind. <b>System question</b> and <b>ticket request</b> are the
                    assistant's job; the other four count as off-topic here.
                  </>
                }
                simpleHint="Every message, sorted by what it was. Only the first two are the assistant's job."
              >
                <BarChart
                  label="Messages by kind"
                  maxRows={8}
                  rows={kinds.map((k) => ({
                    label: kindLabel(k.kind),
                    value: k.turns,
                    detail: [
                      { label: 'Messages', value: num(k.turns) },
                      { label: 'Conversations', value: num(k.conversations) },
                      {
                        label: 'Share of all',
                        value: pct(data.turns ? k.turns / data.turns : null, 1),
                      },
                      { label: 'Ticket offered', value: pct(k.escalation_rate) },
                    ],
                  }))}
                />
              </PanelCard>

              {!simple && (
                <ChartOrTable
                  title="Share that was not a question, by week"
                  hint="A rising line is not a fault — it usually means people are comfortable with the assistant. No documentation will ever answer these."
                  chart={
                    <LineChart
                      label="Share of messages that were not questions, by week"
                      labels={data.weekly.map((w) => weekLabel(w.week))}
                      format={(v) => pct(v)}
                      series={[
                        {
                          name: 'Not a question',
                          color: series()[1],
                          values: data.weekly.map((w) => w.share),
                        },
                      ]}
                    />
                  }
                  table={
                    <Table
                      rows={data.weekly}
                      columns={[
                        { key: 'week', label: 'Week' },
                        { key: 'turns', label: 'Messages', num: true },
                        { key: 'non_system', label: 'Not a question', num: true },
                        {
                          id: 'share',
                          label: 'Share',
                          num: true,
                          render: (r) => pct(r.share, 1),
                          sortValue: (r) => r.share,
                        },
                      ]}
                    />
                  }
                />
              )}
            </Grid>

            {/* No paragraph of totals under this heading: the share, the count and the
                unsorted caveat are the tiles at the top of this same page, and a figure
                printed twice is a figure two things can disagree about. */}
            <PanelCard
              title="What people actually say"
              hint={
                <>
                  The same message counts once. A <b>ticket offered</b> for a greeting is a bug.
                </>
              }
            >
              <Table
                rows={data.messages}
                // Ten at a time. The endpoint returns sixty, and sixty rows of "hii" is a
                // page to scroll past rather than one to read.
                pageSize={10}
                search="Search messages"
                columns={[
                  {
                    id: 'example',
                    label: 'Message',
                    sortValue: (r) => r.example,
                    render: (r) => <ClampedText text={r.example} />,
                  },
                  {
                    id: 'kinds',
                    label: 'Kind',
                    render: (r) =>
                      (r.kinds || '')
                        .split(', ')
                        .filter(Boolean)
                        .map(kindLabel)
                        .join(', ') || '—',
                  },
                  { key: 'occurrences', label: 'Times', num: true },
                  { key: 'users', label: 'People', num: true },
                  {
                    id: 'ticket_offers',
                    label: 'Ticket offered',
                    num: true,
                    sortValue: (r) => r.ticket_offers,
                    render: (r) =>
                      r.ticket_offers ? (
                        <Signal tone="warning">▲ {num(r.ticket_offers)}</Signal>
                      ) : (
                        '—'
                      ),
                  },
                  { id: 'systems', label: 'Where', render: (r) => r.systems || '—' },
                  {
                    id: 'last_seen',
                    label: 'Last seen',
                    render: (r) => shortDate(r.last_seen),
                    sortValue: (r) => r.last_seen,
                  },
                ]}
              />
            </PanelCard>

            {!simple && (
              <PanelCard
                title="Newest first, exactly as typed"
                hint="The raw stream. The grouping above is right for counting and wrong for reading."
              >
                <Table
                  rows={data.recent}
                  pageSize={10}
                  search="Search the stream"
                  columns={[
                    { id: 'ts', label: 'When', render: (r) => shortDate(r.ts), sortValue: (r) => r.ts },
                    {
                      id: 'query',
                      label: 'Message',
                      sortValue: (r) => r.query,
                      render: (r) => <ClampedText text={r.query} />,
                    },
                    {
                      id: 'answer',
                      label: 'What it replied',
                      render: (r) => <AnswerCell text={r.answer} />,
                    },
                    { id: 'kind', label: 'Kind', render: (r) => kindLabel(r.kind) },
                    { key: 'user_role', label: 'Role' },
                    { key: 'system', label: 'System' },
                  ]}
                />
              </PanelCard>
            )}
          </div>
        );
      }}
    </Async>
  );
}
